import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { token } from '@atlaskit/tokens';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';

import { Dimensions } from 'components/visuals';
import {
  fetchIsSiteOnConcession,
  fetchTrackedElsewhere,
  fetchIsOnBehalfOfEmail,
  fetchIsHostedAccountProvisioning,
} from '../duck/actions/accountId';
import { EndUserLicensesExpandedRow } from '../components/endUser/EndUserLicensesExpandedRow';
import { EndUserLicensesCollapsedRow } from '../components/EndUserLicensesCollapsedRow';
import {
  fetchDetails,
  fetchIsRenewableAccount,
  fetchIsUpgradableAccount,
} from '../duck/actions/accounts';
import { MACTableType } from './TableContent';

const CollapseSectionWrapper = styled.tr<{ isArchived?: boolean }>`
  margin-bottom: ${Dimensions.units(1)}px;
  height: ${Dimensions.units(4)}px;
  padding: ${Dimensions.units(0.5)}px ${Dimensions.units(0.5)}px;
  cursor: pointer;
  border-top: solid 2px ${token('color.background.neutral.hovered', '#DCDFE4')};
  ${({ isArchived }) => isArchived && `background-color: ${token('color.border', '#B3B9C4')};`}
`;

const ChildSection = styled.tr<{ isSectionVisible: boolean }>`
  visibility: ${(props) => (props.isSectionVisible ? 'visible' : 'collapse')};
  height: ${(props) => (props.isSectionVisible ? 'unset' : '0')};
  background-color: ${token('color.background.neutral', '#F1F2F4')};
  vertical-align: top !important;
`;

interface Props {
  rowItem: any;
  tableType: MACTableType;
  senNumber?: string;
  isCollapsedAll?: boolean;
  isArchived?: boolean;
  isTrial?: boolean;
  isPartner?: boolean;
}

export const EndUserCollapseRow: React.FC<Props> = ({
  rowItem,
  tableType,
  senNumber,
  isCollapsedAll,
  isArchived,
  isTrial,
}) => {
  const isLicensesSenPreselected =
    tableType === MACTableType.licenses && senNumber && rowItem.accountId === +senNumber;
  const isQuoteIdPreselected =
    (tableType === MACTableType.invoices || tableType === MACTableType.orders) &&
    senNumber &&
    rowItem.orderId === +senNumber;
  const isSENPreselected = isLicensesSenPreselected || isQuoteIdPreselected;

  const [showContent, setShowContent] = useState<boolean>(isSENPreselected || false);
  const lineHeader = useRef(null);
  const dispatch = useDispatch();

  useEffect(() => {
    if (isCollapsedAll) {
      setShowContent(false);
    }
  }, [isCollapsedAll]);

  useLayoutEffect(() => {
    if (isSENPreselected && lineHeader.current) {
      (lineHeader.current as Element).scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  const onSectionToggle = () => {
    if (!showContent) {
      const id = rowItem.parentAccountId || rowItem.accountId || rowItem.licenseId;
      dispatch(fetchDetails(id));
      dispatch(fetchTrackedElsewhere(id));
      dispatch(fetchIsSiteOnConcession(id));
      dispatch(fetchIsRenewableAccount(id));
      dispatch(fetchIsUpgradableAccount(id));
      dispatch(fetchIsOnBehalfOfEmail(id));
      dispatch(fetchIsHostedAccountProvisioning(id));
    }

    setShowContent((prevState) => !prevState);
  };

  return (
    <>
      <CollapseSectionWrapper
        data-cy-mac-end-user-table-row
        onClick={onSectionToggle}
        isArchived={isArchived}
        ref={lineHeader}
      >
        {/* Depending on table type we can control for which page apply the view */}
        {tableType === MACTableType.licenses && (
          <EndUserLicensesCollapsedRow
            showContent={showContent}
            rowItem={rowItem}
            isTrial={isTrial}
          />
        )}
      </CollapseSectionWrapper>
      <ChildSection isSectionVisible={showContent}>
        {tableType === MACTableType.licenses && (
          <EndUserLicensesExpandedRow sen={rowItem.accountId || rowItem.licenseId} />
        )}
      </ChildSection>
    </>
  );
};
